import { useNotify, useTranslate } from 'ra-core';
import { useDropzone } from 'react-dropzone';
import { useState } from 'react';
import { type CsvRecord } from './types';
import { readSpreadsheet } from '../../utils/spreadsheet/read';
import { Button } from '../../components/ui/button';
import { BulkOperationDialogFooter } from './BulkOperationDialogFooter';

interface BulkOperationDialogUploadProps {
  onNext: (data: CsvRecord[]) => void;
  onBack: () => void;
}

export function BulkOperationDialogUpload({
  onBack,
  onNext,
}: BulkOperationDialogUploadProps) {
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [fileContents, setFileContents] = useState<CsvRecord[]>([]);
  const notify = useNotify();
  const translate = useTranslate();

  const dropzone = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xlsx', '.xls'],
    },
    multiple: true,
    onDrop(acceptedFiles) {
      setDroppedFiles(acceptedFiles);
      setFileContents([]);

      Promise.all(acceptedFiles.map((file) => readSpreadsheet(file)))
        .then((results) => results.flatMap((result) => result))
        .then((fileRows) => setFileContents(fileRows))
        .catch(() => {
          notify(
            translate('moonstar.bulk_operation.upload.readError', {
              _: 'Error reading file. Please ensure it is a valid CSV or Excel file.',
            }),
            { type: 'error' }
          );
        });
    },
  });

  return (
    <>
      <div className="flex flex-col gap-3 p-6">
        <div
          {...dropzone.getRootProps()}
          className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/30 p-10 text-center transition-colors hover:border-primary hover:bg-muted/50"
        >
          <input {...dropzone.getInputProps()} />
          {droppedFiles.length > 0 ? (
            <>
              {droppedFiles.map((file) => (
                <p key={file.name} className="font-medium">
                  {file.name}
                </p>
              ))}
              <p className="flex gap-1 text-sm text-muted-foreground">
                <span>
                  {translate('moonstar.bulk_operation.upload.wrongFile', {
                    count: droppedFiles.length,
                  })}
                </span>
                <span className="font-medium text-foreground">
                  {translate('moonstar.bulk_operation.upload.uploadAgain', {
                    _: 'Upload again',
                  })}
                </span>
              </p>
            </>
          ) : (
            <>
              <p className="font-medium">
                {translate('moonstar.bulk_operation.upload.prompt', {
                  _: 'Drag your CSV / Excel file here',
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {translate('moonstar.bulk_operation.upload.browse', {
                  _: 'or browse',
                })}
              </p>
            </>
          )}
        </div>
        {droppedFiles.length > 0 && (
          <p className="text-sm text-muted-foreground">
            {translate('moonstar.bulk_operation.upload.rowsDetected', {
              count: fileContents.length,
            })}
          </p>
        )}
      </div>
      <BulkOperationDialogFooter>
        <Button type="button" variant="secondary" onClick={onBack}>
          {translate('moonstar.bulk_operation.actions.back', { _: 'Back' })}
        </Button>
        <Button
          type="button"
          disabled={fileContents.length === 0}
          onClick={() => onNext(fileContents)}
        >
          {translate('moonstar.bulk_operation.actions.next', { _: 'Next' })}
        </Button>
      </BulkOperationDialogFooter>
    </>
  );
}
